/**
 * Comparison data — kept fact-based and respectful. Pricing is approximate
 * (competitors change tiers frequently); we cite "starting at" + the publicly
 * documented baseline. Always link out to the source where reasonable.
 *
 * Tone: honest. Concede strengths. Win on differentiators. No name-calling.
 */

export const FEATURE_GROUPS = [
  {
    title: "Data & lead discovery",
    rows: [
      { key: "mls_offmarket", label: "MLS + off-market property data" },
      { key: "skip_trace", label: "Skip-tracing built in" },
      { key: "distress_filters", label: "Pre-foreclosure / tax-delinquent filters" },
      { key: "driving_dollars", label: "Driving-for-dollars mobile mode" },
    ],
  },
  {
    title: "Underwriting & analysis",
    rows: [
      { key: "ai_verdict", label: "AI verdict (GO / MAYBE / PASS) on every lead" },
      { key: "multi_strategy", label: "7-strategy analyzer (Wholesale + Novation + Sub-To + Wholetail + Flip + BRRRR + Rental)" },
      { key: "best_exit_ranker", label: "AI Best-Exit ranker — auto-picks the highest-margin strategy" },
      { key: "comps", label: "Sales + rental comps" },
      { key: "branded_reports", label: "Branded public property reports" },
    ],
  },
  {
    title: "Contracts & e-signature",
    rows: [
      { key: "docusign", label: "Send contracts for e-signature from a Pipeline card (DocuSign)" },
      { key: "contract_templates", label: "Assignment, Purchase, JV contract templates with auto-merge" },
      { key: "envelope_tracking", label: "Live envelope status + signed PDFs back in app" },
      { key: "special_conditions_rider", label: "Free-form Special Conditions rider with smart snippet recall" },
      { key: "byo_docusign_templates", label: "Send from your own DocuSign templates — live search + auto role mapping" },
    ],
  },
  {
    title: "AI & universal search",
    rows: [
      { key: "cmd_k_search", label: "Cmd-K palette to jump to any page, lead, buyer, or envelope" },
      { key: "ai_semantic_search", label: "Natural-language AI search (\"open deals in Atlanta under $300k\")" },
      { key: "smart_search_tiles", label: "Pinned AI searches as live, self-refreshing dashboard tiles" },
    ],
  },
  {
    title: "CRM & pipeline",
    rows: [
      { key: "crm", label: "Wholesale CRM" },
      { key: "kanban", label: "Drag-and-drop pipeline" },
      { key: "buyer_match", label: "Auto buyer-match to your cash buyers" },
      { key: "buyer_engagement", label: "Per-buyer photo engagement tracking" },
    ],
  },
  {
    title: "Marketing & growth",
    rows: [
      { key: "landing_pages", label: "Lead-capture landing pages" },
      { key: "yard_sign", label: "Yard-sign QR / 1080×1920 portrait mode" },
      { key: "win_story", label: "Public Win Story brag wall + Wins-of-the-Week podium" },
      { key: "marketing_roi", label: "Marketing ROI tracker — CPL / CAC per channel" },
      { key: "referral", label: "Built-in referral / viral loop" },
    ],
  },
  {
    title: "Community & network",
    rows: [
      { key: "community_wall", label: "Community Deal-Sharing Wall — masked listings + handshake-to-reveal" },
      { key: "community_auto_envelope", label: "Auto-drafted DocuSign Assignment when a buyer is accepted" },
      { key: "community_privacy", label: "Auto-redact phone / email / link in shared notes + EXIF stripping" },
    ],
  },
  {
    title: "Communications & follow-up",
    rows: [
      { key: "managed_numbers", label: "Buy phone numbers directly inside the app — no Twilio setup ($5/mo)" },
      { key: "tcpa_dnc_guard", label: "TCPA-safe outbound — blocks DNC-listed numbers + audit trail" },
      { key: "two_way_sms", label: "Two-way SMS thread on every lead — BYO Twilio (delivery status, polling)" },
      { key: "tap_to_call", label: "Tap-to-call from the lead page (your dialer, your caller ID — no voice charges)" },
      { key: "call_log", label: "Built-in manual call log — outcome chips, duration, notes" },
      { key: "last_contact_badge", label: "Last-contacted urgency badge on every lead row" },
      { key: "followup_filter", label: "Needs-follow-up filter + sidebar red-dot count" },
      { key: "duplicate_detection", label: "Smart fuzzy duplicate detection + merge with undo audit trail" },
      { key: "predictive_dialer", label: "Predictive / power dialer (multi-line outbound)" },
      { key: "voicemail_drop", label: "Ringless voicemail drop campaigns" },
      { key: "ai_lead_response", label: "AI agent that auto-replies to inbound leads" },
    ],
  },
  {
    title: "Team & collaboration",
    rows: [
      { key: "team_seats_included", label: "5 team seats included on Teams tier" },
      { key: "va_seats", label: "Dedicated $19/mo Virtual Assistant seats (restricted role)" },
      { key: "role_based_perms", label: "Role-based permissions (Owner / Admin / Member / VA)" },
      { key: "stripe_seat_prorations", label: "Auto-billed seat overage with Stripe-native prorations" },
    ],
  },
  {
    title: "Automation & follow-up",
    rows: [
      { key: "drip_campaigns", label: "Multi-step email drip campaigns" },
      { key: "drip_auto_exit", label: "Auto-exit drips on recipient reply" },
      { key: "drip_ai_rewrite", label: "AI personalizes every drip step" },
      { key: "team_chat_webhooks", label: "Slack / Discord broadcasts on won deals" },
    ],
  },
];

const NONE = { state: "no" };
const FULL = { state: "yes" };
const PARTIAL = (note) => ({ state: "partial", note });
const YES_NOTE = (note) => ({ state: "yes", note });

export const COMPETITORS = [
  {
    slug: "assignflowos",
    name: "iSpeedToClose",
    self: true, // rendered specially
    tagline: "Speed is the deal — the all-in-one OS for wholesalers + investors",
    price_starting: 0,
    price_starting_label: "Free Starter · $99/mo Pro",
    features: {
      mls_offmarket: FULL,
      skip_trace: PARTIAL("On the roadmap"),
      distress_filters: YES_NOTE("Hustler tier"),
      driving_dollars: PARTIAL("Mobile-responsive; native field-mode in dev"),
      ai_verdict: FULL,
      multi_strategy: FULL,
      best_exit_ranker: FULL,
      comps: FULL,
      branded_reports: FULL,
      docusign: FULL,
      contract_templates: FULL,
      envelope_tracking: FULL,
      special_conditions_rider: FULL,
      byo_docusign_templates: FULL,
      community_wall: FULL,
      community_auto_envelope: FULL,
      community_privacy: FULL,
      managed_numbers: YES_NOTE("$5/mo per number"),
      tcpa_dnc_guard: FULL,
      two_way_sms: FULL,
      tap_to_call: FULL,
      call_log: FULL,
      last_contact_badge: FULL,
      followup_filter: FULL,
      duplicate_detection: FULL,
      predictive_dialer: NONE,
      voicemail_drop: NONE,
      ai_lead_response: NONE,
      team_seats_included: YES_NOTE("Teams tier"),
      va_seats: YES_NOTE("$19/mo each"),
      role_based_perms: FULL,
      stripe_seat_prorations: FULL,
      cmd_k_search: FULL,
      ai_semantic_search: FULL,
      smart_search_tiles: FULL,
      crm: FULL,
      kanban: FULL,
      buyer_match: FULL,
      buyer_engagement: FULL,
      landing_pages: PARTIAL("Capture forms on every public report"),
      yard_sign: FULL,
      win_story: FULL,
      marketing_roi: FULL,
      referral: FULL,
      drip_campaigns: YES_NOTE("Seller / Buyer / Capture audiences"),
      drip_auto_exit: FULL,
      drip_ai_rewrite: YES_NOTE("Hustler+ tier"),
      team_chat_webhooks: FULL,
    },
  },
  {
    slug: "propstream",
    name: "PropStream",
    site: "https://www.propstream.com",
    tagline: "Nationwide property + owner data with skip-tracing",
    price_starting: 99,
    price_starting_label: "from ~$99/mo",
    summary:
      "PropStream is one of the deepest sources of property and owner data in the U.S., trusted by data-heavy operators and list-builders. It pairs powerful filters with skip-tracing and direct-mail integrations.",
    their_strengths: [
      "Industry-leading property and owner data coverage across the U.S.",
      "Mature skip-tracing credits and bulk-list workflows",
      "Strong direct-mail integrations for cold outreach",
      "Familiar tool for list-builders and acquisition teams",
    ],
    where_we_complement: [
      "iSpeedToClose adds AI-driven verdicts so you stop scrolling 500-row lists looking for the deal",
      "Per-buyer photo engagement tracking — see which cash buyer is actually opening your deals",
      "Drag-and-drop pipeline + multi-strategy analyzer (Wholesale + Flip + BRRRR + Rental) built in",
      "Branded public reports, yard-sign QR, and Win Story brag wall — your marketing layer for free",
      "Refer 1 month, get 1 month — built-in viral loop none of the data tools offer",
      "Multi-step email drip campaigns with auto-exit on reply — none of the list-builders ship this",
      "End-to-end contracts inside the same tab — Assignment / Purchase / JV go out for e-signature with one click, status flows back live; PropStream stops at the data",
      "Community Deal-Sharing Wall — turn wrong-market leads into someone else's deal (and your assignment fee) without ever exposing the address until you choose to",
    ],
    ideal_for_them: "Operators whose whole day is list-building + cold direct mail, and who already have a separate CRM.",
    ideal_for_us: "Wholesalers who want fewer tabs — data, AI underwriting, CRM, pipeline, buyer matching, and marketing in one tab.",
    features: {
      mls_offmarket: FULL,
      skip_trace: FULL,
      distress_filters: FULL,
      driving_dollars: PARTIAL("Mobile app"),
      ai_verdict: NONE,
      multi_strategy: PARTIAL("Calculator only"),
      best_exit_ranker: NONE,
      comps: FULL,
      branded_reports: NONE,
      docusign: NONE,
      contract_templates: NONE,
      envelope_tracking: NONE,
      special_conditions_rider: NONE,
      byo_docusign_templates: NONE,
      community_wall: NONE,
      community_auto_envelope: NONE,
      community_privacy: NONE,
      two_way_sms: NONE,
      tap_to_call: NONE,
      call_log: NONE,
      last_contact_badge: NONE,
      followup_filter: NONE,
      duplicate_detection: NONE,
      predictive_dialer: NONE,
      voicemail_drop: NONE,
      ai_lead_response: NONE,
      cmd_k_search: NONE,
      ai_semantic_search: NONE,
      smart_search_tiles: NONE,
      crm: PARTIAL("Light list management"),
      kanban: NONE,
      buyer_match: NONE,
      buyer_engagement: NONE,
      landing_pages: NONE,
      yard_sign: NONE,
      win_story: NONE,
      marketing_roi: NONE,
      referral: NONE,
      drip_campaigns: PARTIAL("Direct-mail focused"),
      drip_auto_exit: NONE,
      drip_ai_rewrite: NONE,
      team_chat_webhooks: NONE,
    },
  },
  {
    slug: "dealmachine",
    name: "DealMachine",
    site: "https://www.dealmachine.com",
    tagline: "Driving-for-dollars + skip-trace + mailers",
    price_starting: 59,
    price_starting_label: "from ~$59/mo",
    summary:
      "DealMachine pioneered the driving-for-dollars mobile workflow. Pin a house from your phone, pull owner info, and trigger a postcard — all in a few taps. Great for boots-on-the-ground wholesalers.",
    their_strengths: [
      "Best-in-class mobile driving-for-dollars (GPS pins, photo capture)",
      "Tight integration between field activity and direct mail postcards",
      "Skip-trace credits bundled into most plans",
      "Strong content + community around mobile prospecting",
    ],
    where_we_complement: [
      "iSpeedToClose analyzes every lead with AI the moment it lands — no need to copy-paste into a separate calculator",
      "Multi-strategy analyzer (Wholesale + Flip + BRRRR + Rental) on every deal, not just wholesale",
      "Public branded reports + per-buyer engagement tracking on the disposition side",
      "Drag-drop wholesale pipeline + automatic buyer matching from your Power Team",
      "Built-in referral viral loop — earn free months as your network grows",
      "Multi-step seller-nurture email drips that auto-stop the moment a seller replies",
      "Contracts + e-signature live in the same deal flow — no copy-paste into DocuSign, no toggling tabs once a lead turns hot",
      "Community Wall — bid on other wholesalers' masked listings or share your own dead leads; handshake-to-reveal keeps both sides safe until acceptance",
    ],
    ideal_for_them: "Boots-on-the-ground wholesalers who drive daily and live in their car.",
    ideal_for_us: "Wholesalers + investors who source from MLS, alerts, and inbound captures — and want analysis + disposition built into the same tool.",
    features: {
      mls_offmarket: PARTIAL("Off-market focus"),
      skip_trace: FULL,
      distress_filters: FULL,
      driving_dollars: FULL,
      ai_verdict: NONE,
      multi_strategy: PARTIAL("Wholesale calc"),
      best_exit_ranker: NONE,
      comps: FULL,
      branded_reports: NONE,
      docusign: NONE,
      contract_templates: NONE,
      envelope_tracking: NONE,
      special_conditions_rider: NONE,
      byo_docusign_templates: NONE,
      community_wall: NONE,
      community_auto_envelope: NONE,
      community_privacy: NONE,
      two_way_sms: NONE,
      tap_to_call: PARTIAL("Mobile-app dialer"),
      call_log: PARTIAL("Call notes in mobile app"),
      last_contact_badge: NONE,
      followup_filter: NONE,
      duplicate_detection: NONE,
      predictive_dialer: NONE,
      voicemail_drop: NONE,
      ai_lead_response: NONE,
      cmd_k_search: NONE,
      ai_semantic_search: NONE,
      smart_search_tiles: NONE,
      crm: FULL,
      kanban: PARTIAL("Stage list view"),
      buyer_match: NONE,
      buyer_engagement: NONE,
      landing_pages: NONE,
      yard_sign: NONE,
      win_story: NONE,
      marketing_roi: NONE,
      referral: NONE,
      drip_campaigns: PARTIAL("Postcard sequences"),
      drip_auto_exit: NONE,
      drip_ai_rewrite: NONE,
      team_chat_webhooks: NONE,
    },
  },
  {
    slug: "reipro",
    name: "REIPro",
    site: "https://www.reipro.com",
    tagline: "Step-by-step investor workflow + document templates",
    price_starting: 109,
    price_starting_label: "from ~$109/mo",
    summary:
      "REIPro is an established investor workflow platform that walks you through deals step by step, with built-in document templates and a contact database. Long-tenured product with a loyal base.",
    their_strengths: [
      "Step-by-step workflow guidance for newer investors",
      "Wide library of contract and document templates",
      "Comp + valuation tools",
      "Established brand with a long track record",
    ],
    where_we_complement: [
      "Modern, mobile-first interface — built for 2026, not a 2010 workflow",
      "AI underwrites every lead automatically, surfacing GO deals in your inbox each morning",
      "Per-buyer engagement scoring + auto-nudges close deals on the disposition side",
      "Branded public reports + Win Story brag wall help your closed deals market themselves",
      "Built-in referral viral loop and Hot Streak leaderboard — community and growth baked in",
      "Modern email drip campaigns with AI-personalized opening lines (Hustler+) — REIPro's drips are template-only",
      "Modern e-signature flow — REIPro ships document templates, iSpeedToClose closes the loop with one-click DocuSign sends, live envelope status, signed PDFs auto-cached, and your own DocuSign account templates pulled in natively",
      "Community Deal-Sharing Wall — the network-effect feature REIPro has never built: masked listings, peer-to-peer assignments, auto-drafted envelopes between two wholesalers",
    ],
    ideal_for_them: "Investors who want a guided, template-heavy walkthrough of each deal.",
    ideal_for_us: "Modern wholesalers who value speed, AI, and automation — and want every tab to feel like a 2026 product.",
    features: {
      mls_offmarket: FULL,
      skip_trace: PARTIAL("Add-on"),
      distress_filters: FULL,
      driving_dollars: NONE,
      ai_verdict: NONE,
      multi_strategy: PARTIAL("Workflow calculators"),
      best_exit_ranker: NONE,
      comps: FULL,
      branded_reports: NONE,
      docusign: PARTIAL("Document templates only — no e-sign"),
      contract_templates: FULL,
      envelope_tracking: NONE,
      special_conditions_rider: NONE,
      byo_docusign_templates: NONE,
      community_wall: NONE,
      community_auto_envelope: NONE,
      community_privacy: NONE,
      two_way_sms: NONE,
      tap_to_call: NONE,
      call_log: NONE,
      last_contact_badge: NONE,
      followup_filter: NONE,
      duplicate_detection: NONE,
      predictive_dialer: NONE,
      voicemail_drop: NONE,
      ai_lead_response: NONE,
      cmd_k_search: NONE,
      ai_semantic_search: NONE,
      smart_search_tiles: NONE,
      crm: FULL,
      kanban: PARTIAL("Stage tracking"),
      buyer_match: PARTIAL("Buyer list"),
      buyer_engagement: NONE,
      landing_pages: NONE,
      yard_sign: NONE,
      win_story: NONE,
      marketing_roi: NONE,
      referral: NONE,
      drip_campaigns: FULL,
      drip_auto_exit: NONE,
      drip_ai_rewrite: NONE,
      team_chat_webhooks: NONE,
    },
  },
  {
    slug: "resimpli",
    name: "REsimpli",
    site: "https://resimpli.com",
    tagline: "All-in-one wholesaler CRM with dialer, RVM, AI agents, and KPI tracking",
    price_starting: 99,
    price_starting_label: "from ~$99/mo",
    summary:
      "REsimpli is the most direct competitor to iSpeedToClose — an all-in-one wholesaler CRM that ships a built-in dialer, ringless voicemail, two-way SMS (managed numbers), follow-up automation, AI lead-response agents, driving-for-dollars, and a private investor community. They lead the market on telephony depth and AI automation; we lead on the Community Wall → auto-DocuSign workflow + CRM hygiene (dupe detection + merge + undo).",
    their_strengths: [
      "Predictive dialer + call recording + ringless voicemail built in",
      "AI agents that auto-respond to inbound leads",
      "Two-way SMS with managed numbers (no Twilio setup)",
      "Multi-channel follow-up automation (SMS, email, RVM, direct mail)",
      "Driving-for-Dollars mobile app integrated with CRM",
      "REsimpli University — built-in training + private investor community",
    ],
    where_we_complement: [
      "Our Community Wall → auto-DocuSign envelope workflow is unique — list a deal anonymously, accept a buyer, the Assignment contract pre-fills both parties from the listing context",
      "BYO Twilio means you keep your own number relationship and pay no SMS markup to us",
      "Smart fuzzy duplicate detection + merge + undo audit trail keeps your CRM clean — REsimpli doesn't pitch this",
      "Branded buyer reports + buyer-match alerts are first-class, not an afterthought",
      "Pro tier at $99/mo includes the integrated bundle — REsimpli's comparable tier starts higher",
    ],
    ideal_for_them: "Wholesalers who need deep telephony (predictive dialer, RVM campaigns, AI lead response) and are willing to pay for the all-in-one bundle.",
    ideal_for_us: "Wholesalers who want the Community Wall + DocuSign workflow as a closing edge, prefer BYO Twilio over managed SMS, and value CRM hygiene polish.",
    features: {
      mls_offmarket: PARTIAL("Skip-traced lists"),
      skip_trace: FULL,
      distress_filters: FULL,
      driving_dollars: FULL,
      ai_verdict: NONE,
      multi_strategy: NONE,
      best_exit_ranker: NONE,
      comps: PARTIAL("Basic comps"),
      branded_reports: NONE,
      docusign: PARTIAL("E-sign via integration"),
      contract_templates: PARTIAL("Generic templates"),
      envelope_tracking: NONE,
      special_conditions_rider: NONE,
      byo_docusign_templates: NONE,
      community_wall: PARTIAL("Private mastermind community"),
      community_auto_envelope: NONE,
      community_privacy: NONE,
      two_way_sms: FULL,
      tap_to_call: FULL,
      call_log: FULL,
      last_contact_badge: NONE,
      followup_filter: PARTIAL("Multi-channel drip"),
      duplicate_detection: NONE,
      predictive_dialer: FULL,
      voicemail_drop: FULL,
      ai_lead_response: FULL,
      cmd_k_search: NONE,
      ai_semantic_search: NONE,
      smart_search_tiles: NONE,
      crm: FULL,
      kanban: FULL,
      buyer_match: PARTIAL("Cash buyer search"),
      buyer_engagement: NONE,
      landing_pages: NONE,
      yard_sign: NONE,
      win_story: NONE,
      marketing_roi: PARTIAL("KPI dashboard"),
      referral: NONE,
      drip_campaigns: FULL,
      drip_auto_exit: NONE,
      drip_ai_rewrite: NONE,
      team_chat_webhooks: NONE,
    },
  },
  {
    slug: "carrot",
    name: "Carrot",
    site: "https://carrot.com",
    tagline: "Lead-capture landing pages + investor SEO",
    price_starting: 99,
    price_starting_label: "from ~$99/mo",
    summary:
      "Carrot is the best-known landing-page builder for real-estate investors and wholesalers. Templates, motivated-seller forms, and SEO content help capture inbound leads from Google.",
    their_strengths: [
      "Battle-tested landing-page templates for motivated sellers + cash buyers",
      "Strong investor SEO playbook and content library",
      "Easy domain + lead-form setup",
      "Big training community for inbound marketing",
    ],
    where_we_complement: [
      "Carrot brings the lead in — iSpeedToClose underwrites, manages, and dispositions it",
      "AI verdict the moment a lead hits your inbox, with comps and a strategy view",
      "Pipeline, buyer matching, engagement tracking, branded reports — everything that happens after the form is filled",
      "Capture forms are also embedded on every iSpeedToClose public report, so your closed deals capture buyer leads too",
      "Many wholesalers run Carrot for capture and iSpeedToClose for ops — they're complementary, not rivals",
      "Once a capture comes in, iSpeedToClose auto-nurtures it with a 3-step email drip that exits the moment they reply",
      "Contracts close the loop after Carrot captures the lead — Assignment / Purchase / JV send for e-signature in 30 seconds; signed PDFs back in the same dashboard",
      "Community Wall — the leads Carrot can't help you close (wrong state, wrong asset) become assignment fees instead of dead rows in your CRM",
    ],
    ideal_for_them: "Wholesalers who want a polished inbound landing-page + SEO funnel.",
    ideal_for_us: "Everything that happens after the lead arrives — analysis, CRM, pipeline, disposition, marketing your wins.",
    features: {
      mls_offmarket: NONE,
      skip_trace: NONE,
      distress_filters: NONE,
      driving_dollars: NONE,
      ai_verdict: NONE,
      multi_strategy: NONE,
      best_exit_ranker: NONE,
      comps: NONE,
      branded_reports: NONE,
      docusign: NONE,
      contract_templates: NONE,
      envelope_tracking: NONE,
      special_conditions_rider: NONE,
      byo_docusign_templates: NONE,
      community_wall: NONE,
      community_auto_envelope: NONE,
      community_privacy: NONE,
      two_way_sms: NONE,
      tap_to_call: NONE,
      call_log: NONE,
      last_contact_badge: NONE,
      followup_filter: NONE,
      duplicate_detection: NONE,
      predictive_dialer: NONE,
      voicemail_drop: NONE,
      ai_lead_response: NONE,
      cmd_k_search: NONE,
      ai_semantic_search: NONE,
      smart_search_tiles: NONE,
      crm: PARTIAL("Lead inbox"),
      kanban: NONE,
      buyer_match: NONE,
      buyer_engagement: NONE,
      landing_pages: FULL,
      yard_sign: NONE,
      win_story: NONE,
      marketing_roi: NONE,
      referral: NONE,
      drip_campaigns: PARTIAL("Form auto-responder"),
      drip_auto_exit: NONE,
      drip_ai_rewrite: NONE,
      team_chat_webhooks: NONE,
    },
  },
];

export const COMPETITORS_BY_SLUG = Object.fromEntries(
  COMPETITORS.map((c) => [c.slug, c]),
);

// Everything that isn't iSpeedToClose itself — used by the hub matrix + index.
export const RIVALS = COMPETITORS.filter((c) => !c.self);

export const ASSIGNFLOW = COMPETITORS_BY_SLUG.assignflowos;
